import { useClient } from "#/hook/useClient";
import type { HawbDetail } from "#/types/manifest";
import { formatDateTime } from "#/utils/dateTimeUtils";
import { formatHawbIssue, formatHawbLabel } from "#/utils/manifestUtils";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface ExitIssueBoxProps {
  hawb: HawbDetail;
}

/** Red box explaining, in plain words, every problem of the last Validate exit. */
const ExitIssueBox = ({ hawb }: ExitIssueBoxProps) => {
  const { t } = useTranslation();
  const { client } = useClient();
  if (hawb.exit_status !== "issue") return null;
  const issues = hawb.exit_issues ?? [];
  const exit = hawb.last_exit;

  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
      <IconAlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600" />
      <div className="flex flex-col gap-1">
        <span className="font-semibold text-red-700">
          {t("manifests.detail.issueTitle", { label: formatHawbLabel(hawb) })}
        </span>
        <ul className="list-disc pl-5 text-sm text-red-700">
          {issues.map((issue) => (
            <li key={issue.code}>
              {issue.code === "partial"
                ? t("manifests.detail.issueLines.partial", {
                    read: exit?.read_count ?? 0,
                    expected: exit?.expected_count ?? hawb.packages,
                    count: issue.count ?? 0,
                  })
                : t(`manifests.detail.issueLines.${issue.code}`, {
                    count: issue.count ?? 0,
                    defaultValue: formatHawbIssue(issue, t),
                  })}
            </li>
          ))}
        </ul>
        {exit && (
          <span className="mt-1 text-sm text-red-800">
            {t("manifests.detail.exitSession", {
              device: exit.device_name ?? t("manifests.detail.unknownDevice"),
              time: formatDateTime(exit.validated_at, client),
            })}
            {exit.offline ? ` · ${t("manifests.detail.offline")}` : ""}
          </span>
        )}
      </div>
    </div>
  );
};

export default ExitIssueBox;
